/**
 * dates.js — "today", "tomorrow" and "Wednesday", meant the way people mean them.
 *
 * Both the membership banner and the week line under your name need to say how
 * far off a kickoff is, and both got it wrong the same way: they divided the
 * milliseconds between now and then by 86,400,000 and rounded. That counts
 * twenty four hour blocks, and nobody means a twenty four hour block by "today".
 *
 * On Wednesday afternoon, six hours before an 8:20 PM kickoff, block arithmetic
 * returns 1 and the app says the season starts TOMORROW on the day it starts.
 * That is the one day of the year that sentence has to be right.
 *
 * So the comparison is between calendar days in the reader's own timezone, which
 * is also the timezone the times are printed in. Same day is 0 however few hours
 * are left in it.
 */

function startOfLocalDay(d) {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.getTime();
}

/**
 * Whole calendar days from today until `iso`, or null if there is no usable date.
 * 0 is today however late in it, 1 is tomorrow, negatives are in the past.
 */
export function calendarDaysUntil(iso) {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((startOfLocalDay(d) - startOfLocalDay(new Date())) / 86400000);
}

/**
 * When a kickoff is, said the way somebody would say it out loud:
 * "today at 8:20 PM", "tomorrow at 8:20 PM", "Wednesday at 8:20 PM", "September 21".
 *
 * The reader's own clock on purpose. A kickoff is a wall clock moment to
 * everybody, and a time in somebody else's zone is a time they have to do
 * arithmetic on. Past a week it becomes a date, because "starts Wednesday" is
 * useless when there are three Wednesdays between now and then.
 */
export function kickoffPhrase(iso) {
  const days = calendarDaysUntil(iso);
  if (days === null) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (days <= 0) return `today at ${time}`;
  if (days === 1) return `tomorrow at ${time}`;
  if (days <= 6) return `${d.toLocaleDateString(undefined, { weekday: 'long' })} at ${time}`;
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}
